// Persists a document edit. The caller resolves contentType (block-editor flag
// or an override) and passes the value in; this core never reads a flag.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { DocumentGenType, type ContentType } from '@heidi/schema/ml-scribe';

import {
  GET_DOCUMENT_BY_ID,
  GET_DOCUMENTS_BY_ID,
  UPDATE_DOCUMENT_BY_ID,
  type QueryDocumentResponse,
} from '../../contracts';
import { updateDocument } from '../api/documents';

type UpdateDocumentInput<TAttempt> = {
  content: string;
  sessionId: string;
  documentId: string;
  /** Set by an autosave cycle: the request emits a document_update attempt. */
  autosaveAttempt?: TAttempt;
};

export function useMutateUpdateSyncDocumentById<TAttempt>({
  contentType,
  trackAttempt,
  onUpdateError,
}: {
  contentType: ContentType;
  trackAttempt?: (attempt: TAttempt, save: () => Promise<unknown>) => Promise<unknown>;
  onUpdateError?: (
    error: Error,
    variables: { sessionId: string; documentId: string }
  ) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_DOCUMENT_BY_ID],
    mutationFn: async ({
      content,
      sessionId,
      documentId,
      autosaveAttempt,
    }: UpdateDocumentInput<TAttempt>) => {
      const document = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      const save = () =>
        updateDocument({
          sessionId,
          documentId,
          name: document?.name || '',
          content,
          generationType: document?.generation_type || DocumentGenType.TEMPLATE,
          updatedDictation: document?.updated_dictation || '',
          contentType,
        });

      return autosaveAttempt && trackAttempt
        ? trackAttempt(autosaveAttempt, save)
        : save();
    },
    onMutate: async ({ sessionId, documentId, content }) => {
      await queryClient.cancelQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      const previousDocument = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      queryClient.setQueryData<QueryDocumentResponse>(
        [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            draft.content = content;
            draft.content_type = contentType;
          });
        }
      );

      return { previousDocument };
    },
    onSuccess: async (_, { sessionId, documentId }) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      await queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId }],
      });
    },
    onError: (error, { sessionId, documentId }) => {
      onUpdateError?.(error, { sessionId, documentId });
    },
  });
}
